/* eslint-disable jira/restricted/fedramp-isolated-cloud */

import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { SidebarIdBoardSettings } from '@jira/navigation-apps__constants/src/SidebarIdBoardSettings.tsx';

import {
	getRapidboardResources,
	getRapidBoardDataResource,
	shouldLoadUIFBoardResources,
} from '@jira/router-resources__classic-projects/src/getRapidboardResources.tsx';
import { getBoardDetailsResource } from '@jira/router-resources__classic-projects/src/getBoardDetailsResource.tsx';
import { getEditModelResource } from '@jira/router-resources__classic-projects/src/getEditModelResource.tsx';
import { isFedRamp } from '@atlassian/atl-context';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { ClassicBoardSkeleton } from '@jira/platform__skeletons/src/ui/classic-board/ClassicBoardSkeleton.tsx';
import { classicSoftwareBoardSettingsRoute } from '@jira/router-routes__software-classic-board-settings-routes/src/classicSoftwareBoardSettingsRoute.tsx';
import { genericProjectLayout } from '@jira/platform__layouts-generic-project-layout/src/index.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { incidentExperimentTraitResource } from '@jira/router-resources__software-incidents/src/incidentExperimentTraitResource.tsx';
import {
	forgeBoardModuleResource,
	forgeProjectModuleResource,
} from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { uifBoardResource } from '@jira/router-resources__uif-board/src/uifBoardResource.tsx';
import { nextBestTaskFeatureResource } from '@jira/router-resources__next-best-task/src/nextBestTaskFeatureResource.tsx';
import {
	CLASSIC_BOARD,
	COMPANY_MANAGED_BOARD,
} from '@jira/platform__observability-capabilities/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	boardSettingsPageEntryPoint,
	GSNBoardSettingsPageEntryPoint,
} from './ui/page-entry-point/index.tsx';
import type { Route } from '@atlassian/react-resource-router';

export const classicSoftwareBoardSettingsRouteEntry: Route = createEntry(
	classicSoftwareBoardSettingsRoute,
	() => ({
		group: 'classic-software',
		ufoName: 'classic-board',
		skeleton: ClassicBoardSkeleton,
		entryPoint: () =>
			fg('navx-global-side-nav') ? GSNBoardSettingsPageEntryPoint : boardSettingsPageEntryPoint,

		navigation: {
			menuId: MenuIdProjects,
			sidebarId: SidebarIdBoardSettings,
		},

		layout: genericProjectLayout,

		resources: [
			...getNavigationResources(),
			// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them if needed
			// A guide to check & cleanup: https://hello.atlassian.net/wiki/x/Q8XybwE
			incidentExperimentTraitResource,
			// eslint-disable-next-line jira/router/no-project-context-resource
			projectContextResource,
			forgeProjectModuleResource,
			staticProjectSidebarNavigationResource,
			staticProjectSidebarConnectAddonsNavigationResource,
			// end of resources expanded from getNavigationSidebarProjectResource
			...getRapidboardResources(),
			getRapidBoardDataResource(),
			resourceWithCondition(shouldLoadUIFBoardResources, uifBoardResource),
			// Load the NBT resource if we opt-in UIF, not fedRamp is enabled
			resourceWithCondition(
				() =>
					shouldLoadUIFBoardResources() &&
					(fg('aem_rr_classic_board_settings')
						? isFeatureEnabled('jira', 'jira-next-best-task')
						: !isFedRamp()),
				nextBestTaskFeatureResource,
			),
			getEditModelResource(),
			getBoardDetailsResource(),
			forgeBoardModuleResource,
		],

		meta: {
			// Inline this statement instead of creating a (circular) dependency by importing
			// { classicBoardCapability } from '@jira/router-routes__software-classic-board-entries/src/common/constants.tsx';
			capability: !__SERVER__ ? COMPANY_MANAGED_BOARD : CLASSIC_BOARD,
		},

		bifrost: {
			bifrostGroup: 'shell-only',
		},
	}),
);
